/**
 * Découpe un exercice V2 (blocs) en ÉTAPES jouables du Live School. Module
 * PUR et DÉCOUPLÉ (type de bloc minimal local — ne dépend pas de `apps/app`).
 * Le même algorithme est mirroré côté Deno dans `_shared/live-steps.ts`.
 *
 * Modèle V2 (cf. block-helpers d'apps/app) : une `statement_question` a des
 * enfants `correction` (les checkpoints QCM, repérés par `data.answer_md`), et
 * chaque `correction` a des enfants `distractor` (`data.label_md` = mauvais
 * choix). Une étape live = UN checkpoint : la bonne réponse est la `correction`
 * (label = `answer_md`), les leurres sont ses `distractor`s.
 */

/** Bloc V2 minimal nécessaire à la découpe (sous-ensemble de `ExerciseBlock`). */
export interface LiveSourceBlock {
  id: string;
  parent_id: string | null;
  block_type: string;
  section: string;
  position: number;
  data: Record<string, unknown>;
}

export interface LiveChoice {
  blockId: string;
  labelMd: string;
  isCorrect: boolean;
}

/** Un indice cliquable de la chasse aux indices attachée à une question. */
export interface LiveClue {
  id: string;
  sourceText: string;
  revealedMd: string;
  xpReward: number;
}

/** Bloc `clue_hunt` enfant d'une question (si présent). */
export interface LiveClueHunt {
  introMd: string;
  clues: LiveClue[];
}

/** Bloc de contexte (`statement`) affiché avant une question : texte + visuels. */
export interface LiveContentBlock {
  contentMd: string;
  /** Visuels V2 embarqués (`data.visuals`), passés tels quels au renderer. */
  visuals: unknown[];
}

export interface LiveStep {
  /** Index 0-based de l'étape dans le défi. */
  stepIndex: number;
  questionBlockId: string;
  /** Le `correction` qui porte la bonne réponse PRIMAIRE (clé serveur). */
  correctionBlockId: string;
  /**
   * Ensemble complet des blocs corrects : la `correction` + les `distractor`
   * marqués `data.is_correct` (QCM à réponses multiples). En mode single, ne
   * contient que `correctionBlockId`. Clé serveur du scoring tout-ou-rien.
   */
  correctBlockIds: string[];
  /** 'single' (clic direct) ou 'multiple' (cases à cocher + Valider). */
  answerMode: 'single' | 'multiple';
  /**
   * Contexte introduit AVANT cette question (blocs `statement`) : la situation
   * (au step 0) puis les énoncés intermédiaires. Vide pour les checkpoints
   * suivants d'une même question (évite la duplication à l'affichage accumulé).
   */
  intro: LiveContentBlock[];
  /** Sous-énoncé propre à la question (`statement_question.sub_statement_md`). */
  subStatementMd: string;
  /** Énoncé affiché : prompt de la question. */
  promptMd: string;
  /** Visuels de la question (`statement_question.data.visuals`). */
  promptVisuals: unknown[];
  /** Choix proposés : la bonne `correction` + ses distracteurs (ordre canonique). */
  choices: LiveChoice[];
  /** Chasse aux indices attachée à cette question (bloc `clue_hunt` enfant).
      Optionnel — la majorité des questions n'en a pas. Le chrono côté chasse
      est CELUI DU STEP LIVE (30 s), pas un chrono propre. */
  clueHunt?: LiveClueHunt;
}

function str(data: Record<string, unknown>, key: string): string {
  const v = data[key];
  return typeof v === 'string' ? v : '';
}

/**
 * Premier champ texte non vide parmi `keys`. Tolère les exercices dont les blocs
 * n'utilisent pas la clé canonique (ex. seeds/DAG qui stockent le texte dans
 * `markdown`, ou distracteurs sous `answer_md` au lieu de `label_md`).
 */
function firstStr(data: Record<string, unknown>, keys: readonly string[]): string {
  for (const k of keys) {
    const v = data[k];
    if (typeof v === 'string' && v.length > 0) return v;
  }
  return '';
}

const STATEMENT_KEYS = ['content_md', 'markdown'] as const;
const PROMPT_KEYS = ['prompt_md', 'markdown'] as const;
const CORRECT_KEYS = ['answer_md', 'markdown'] as const;
const DISTRACTOR_KEYS = ['label_md', 'answer_md', 'markdown'] as const;

/** Visuels V2 embarqués d'un bloc (`data.visuals[]`), ou tableau vide. */
function visualsOf(data: Record<string, unknown>): unknown[] {
  const v = data['visuals'];
  return Array.isArray(v) ? v : [];
}

/** Blocs de contexte (`statement`) triés par position. */
function listStatements(blocks: readonly LiveSourceBlock[]): LiveSourceBlock[] {
  return blocks.filter((b) => b.block_type === 'statement').sort((a, b) => a.position - b.position);
}

function childrenOf(blocks: readonly LiveSourceBlock[], parentId: string): LiveSourceBlock[] {
  return blocks.filter((b) => b.parent_id === parentId).sort((a, b) => a.position - b.position);
}

function isQcmCheckpoint(b: LiveSourceBlock): boolean {
  return (
    b.block_type === 'correction' &&
    b.parent_id !== null &&
    firstStr(b.data, CORRECT_KEYS).length > 0
  );
}

/** Questions (`statement_question`) triées par position. */
function listQuestions(blocks: readonly LiveSourceBlock[]): LiveSourceBlock[] {
  return blocks
    .filter((b) => b.block_type === 'statement_question')
    .sort((a, b) => a.position - b.position);
}

/**
 * Cherche un bloc `clue_hunt` parmi les enfants d'une question et le convertit
 * en `LiveClueHunt` sérialisable. Renvoie undefined si absent ou vide.
 */
function parseClueHunt(children: readonly LiveSourceBlock[]): LiveClueHunt | undefined {
  const ch = children.find((b) => b.block_type === 'clue_hunt');
  if (!ch) return undefined;
  const introMd = str(ch.data, 'intro_md');
  const rawClues = ch.data['clues'];
  if (!Array.isArray(rawClues) || rawClues.length === 0) return undefined;
  const clues: LiveClue[] = [];
  for (const raw of rawClues) {
    if (!raw || typeof raw !== 'object') continue;
    const r = raw as Record<string, unknown>;
    const id = typeof r.id === 'string' ? r.id : '';
    const sourceText = typeof r.source_text === 'string' ? r.source_text : '';
    const revealedMd = typeof r.revealed_md === 'string' ? r.revealed_md : '';
    const xpReward = typeof r.xp_reward === 'number' ? r.xp_reward : 5;
    if (!id || !sourceText || !revealedMd) continue;
    clues.push({ id, sourceText, revealedMd, xpReward });
  }
  if (clues.length === 0) return undefined;
  return { introMd, clues };
}

export interface BuildLiveStepsOptions {
  /** Nombre maximum d'étapes (le défi en vise 10). Défaut 10. */
  maxSteps?: number;
}

/**
 * Construit les étapes jouables, dans l'ordre (questions × checkpoints aplatis),
 * bornées à `maxSteps`. Une étape sans distracteur est IGNORÉE (un QCM a besoin
 * d'au moins un leurre). Choix en ordre canonique (bonne réponse d'abord) — le
 * mélange est délégué à `shuffleChoices` (déterministe) au moment du chiffrement.
 */
export function buildLiveSteps(
  blocks: readonly LiveSourceBlock[],
  { maxSteps = 10 }: BuildLiveStepsOptions = {},
): LiveStep[] {
  const steps: LiveStep[] = [];
  const statements = listStatements(blocks);
  // Position de la dernière question ÉMISE : les `statement` entre elle et la
  // question courante sont le contexte introduit à cette étape (situation au
  // step 0). Non avancé pour une question sans étape → son contexte roule vers
  // la suivante plutôt que d'être perdu.
  let introCursor = Number.NEGATIVE_INFINITY;

  for (const question of listQuestions(blocks)) {
    const checkpoints = childrenOf(blocks, question.id).filter(isQcmCheckpoint);
    let emitted = false;
    for (const checkpoint of checkpoints) {
      const distractors = childrenOf(blocks, checkpoint.id).filter(
        (b) => b.block_type === 'distractor',
      );
      if (distractors.length === 0) continue; // pas de QCM jouable sans leurre.

      // Réponses multiples : un distracteur `is_correct` est une bonne réponse.
      const correctBlockIds = [
        checkpoint.id,
        ...distractors.filter((d) => d.data['is_correct'] === true).map((d) => d.id),
      ];
      const correctSet = new Set(correctBlockIds);
      const answerMode: 'single' | 'multiple' =
        checkpoint.data['answer_mode'] === 'multiple' ? 'multiple' : 'single';

      const choices: LiveChoice[] = [
        {
          blockId: checkpoint.id,
          labelMd: firstStr(checkpoint.data, CORRECT_KEYS),
          isCorrect: true,
        },
        ...distractors.map((d) => ({
          blockId: d.id,
          labelMd: firstStr(d.data, DISTRACTOR_KEYS),
          isCorrect: correctSet.has(d.id),
        })),
      ];

      // Contexte uniquement sur le PREMIER checkpoint de la question.
      const intro: LiveContentBlock[] = emitted
        ? []
        : statements
            .filter((s) => s.position > introCursor && s.position < question.position)
            .map((s) => ({
              contentMd: firstStr(s.data, STATEMENT_KEYS),
              visuals: visualsOf(s.data),
            }));

      // Chasse aux indices enfant de la question — attachée uniquement au
      // 1er checkpoint (les suivants d'une même question la partageraient).
      const clueHunt = emitted ? undefined : parseClueHunt(childrenOf(blocks, question.id));

      steps.push({
        stepIndex: steps.length,
        questionBlockId: question.id,
        correctionBlockId: checkpoint.id,
        correctBlockIds,
        answerMode,
        intro,
        subStatementMd: str(question.data, 'sub_statement_md'),
        promptMd: firstStr(question.data, PROMPT_KEYS),
        promptVisuals: visualsOf(question.data),
        choices,
        ...(clueHunt ? { clueHunt } : {}),
      });
      emitted = true;
      if (steps.length >= maxSteps) return steps;
    }
    if (emitted) introCursor = question.position;
  }
  return steps;
}

/** Combien d'étapes jouables un exercice fournit (≤ maxSteps). */
export function countLiveSteps(blocks: readonly LiveSourceBlock[], maxSteps = 10): number {
  return buildLiveSteps(blocks, { maxSteps }).length;
}

// ── Mélange déterministe des choix (reproductible serveur ⇄ tests) ────────────

/** Hash 32-bit déterministe d'une chaîne (FNV-1a). */
function hashSeed(seed: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** PRNG déterministe (mulberry32). */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Mélange déterministe (Fisher-Yates seedé) — même `seed` → même ordre. Sert à
 * positionner la bonne réponse à un index variable mais reproductible (le
 * serveur chiffre avec ce mélange ; aucune fuite via l'ordre). Pur.
 */
export function shuffleChoices<T>(choices: readonly T[], seed: string): T[] {
  const out = [...choices];
  const rng = mulberry32(hashSeed(seed));
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = out[i]!;
    out[i] = out[j]!;
    out[j] = tmp;
  }
  return out;
}
